import React, { useState } from "react";

// react-bootstrap components
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  InputGroup,
  Modal,
  Row,
  Tab,
  Tabs,
} from "react-bootstrap";
import Select from "react-select";
// core components
import ReactTable from "components/ReactTable/ReactTable.js";
import { Query, useMutation } from "react-apollo";
import { GET_CLIENT } from "./queries";

import map from "lodash/map";
import find from "lodash/find";
import unset from "lodash/unset";
import head from "lodash/head";
import set from "lodash/set";
import cloneDeep from "lodash/cloneDeep";
import concat from "lodash/concat";
import includes from "lodash/includes";
import withSession from "./withSession";
import {
  ADD_CHALLENGE_TO_CLIENT,
  UPDATE_CHALLENGE,
  DELETE_CHALLENGE,
  IMAGE_UPLOAD,
  PUBLISH_CHALLENGE,
  SEND_NOTIFICATIONS,
  ADD_NOTIFICATION_TO_CLIENT,
  UPDATE_NOTIFICATION,
  DELETE_NOTIFICATION,
} from "./mutations";
import Dropzone from "react-dropzone";
import ReactQuill from "react-quill";
import { modules } from "constants/reactQuill";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import "./Forms/SignUpWizard/datePicker.css";

const MAXSIZE = 100000000; //bytes
const FILETYPES = ["application/pdf", "image/*"];

const options = [
  { value: "everyone", label: "Everyone" },
  { value: "coaches", label: "Coaches" },
  { value: "challengeAll", label: "Challenge - Everyone" },
  { value: "challengeCoach", label: "Challenge - Coaches" },
];

function NotificationManager(props) {
  const [modal, setModal] = React.useState(false);

  const [title, setTitle] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [sendTo, setSendTo] = React.useState({
    value: "everyone",
    label: "Everyone",
  });
  const [selectedChallenge, setSelectedChallenge] = React.useState(null);
  const [frequency, setFrequency] = React.useState("none");
  const [time, setTime] = React.useState(8);
  const [date, setDate] = React.useState(null);
  const [showError, setShowError] = React.useState("");
  const [showErrorConfirm, setShowErrorConfirm] = React.useState("");

  const [editNotificationId, setEditNotificationId] = React.useState("");
  const [deleteNotificationId, setDeleteNotificationId] = React.useState("");

  const handleOpenEdit = (notification) => {
    setModal(true);
    setEditNotificationId(notification?._id);
    setTitle(notification?.title);
    setMessage(notification?.message);
    setSendTo(
      find(options, (option) => {
        return option.label === notification?.sendTo;
      })
    );
    if (notification?.filters?.challengeId)
      setSelectedChallenge(notification?.filters?.challengeId);
    setFrequency(notification?.frequency);
    setTime(notification?.time);
    setDate(notification?.date);
  };

  const handleOpenDelete = (notification) => {
    // setDeleteModal(true)
    setDeleteNotificationId(notification._id);
  };

  const handleCloseDelete = () => {
    // setDeleteModal(false)
    setDeleteNotificationId("");
  };

  const handleClose = () => {
    setModal(false);
    setEditNotificationId("");
    setDeleteNotificationId("");
    setTitle("");
    setMessage("");
    setSendTo({ value: "everyone", label: "Everyone" });
    setSelectedChallenge(null);
    setFrequency("none");
    setTime(8);
    setDate(null);
  };

  const [AddNotificationToClient] = useMutation(ADD_NOTIFICATION_TO_CLIENT);
  const [UpdateNotification] = useMutation(UPDATE_NOTIFICATION);
  const [DeleteNotification] = useMutation(DELETE_NOTIFICATION);
  const [sendNotifications] = useMutation(SEND_NOTIFICATIONS);

  const handleAdd = () => {
    AddNotificationToClient({
      variables: {
        name: props.session.me.clientName,
        notificationInput: {
          title,
          message,
          frequency,
          time,
          date,
          filters: {
            challengeId: includes(sendTo?.label, "Challenge")
              ? selectedChallenge?._id
              : undefined,
            isCoach: includes(sendTo?.label, "Coach"),
          },
        },
      },
    }).then(() => {
      handleClose();
    });
  };

  const handleUpdate = () => {
    UpdateNotification({
      variables: {
        name: props.session.me.clientName,
        notificationId: editNotificationId,
        notificationInput: {
          title,
          message,
          frequency,
          time,
          date,
          filters: {
            challengeId: includes(sendTo?.label, "Challenge")
              ? selectedChallenge?._id
              : undefined,
            isCoach: includes(sendTo?.label, "Coach"),
          },
        },
      },
    }).then(() => {
      handleClose();
    });
  };

  const handleDelete = () => {
    DeleteNotification({
      variables: {
        name: props.session.me.clientName,
        notificationId: deleteNotificationId,
      },
    }).then(() => {
      handleClose();
    });
  };

  const handleSend = () => {
    sendNotifications({
      variables: {
        name: props.session.me.clientName,
        notificationDetails: {
          title,
          message,
        },
        notificationFilters: {
          challengeId: includes(sendTo?.label, "Challenge")
            ? selectedChallenge?._id
            : undefined,
          isCoach: includes(sendTo?.label, "Coach"),
        },
      },
    }).then(() => {
      handleClose();
    });
  };

  return (
    <Query
      query={GET_CLIENT}
      variables={{
        name: props.session.me.clientName,
      }}
    >
      {({ loading, data, refetch }) => {
        if (loading) return "Loading...";
        const { getClient } = data;
        const { challenges, notifications } = getClient || {};

        const tableData = map(notifications, (notification, key) => {
          let sendTo = "Everyone";
          if (notification?.filters?.isCoach) {
            if (notification?.filters?.challengeId) {
              sendTo = "Challenge - Coaches";
            } else {
              sendTo = "Coaches";
            }
          } else if (notification?.filters?.challengeId) {
            sendTo = "Challenge - Everyone";
          }
          return {
            id: key,
            ...notification,
            sendTo,
          };
        });
        return (
          <>
            <Container fluid>
              <Row>
                <Col md="12">
                  <h4 className="title">Notification Manager</h4>
                  <Card>
                    <Card.Body>
                      <ReactTable
                        data={tableData}
                        columns={[
                          {
                            header: "Title",
                            accessorKey: "title",
                          },
                          {
                            header: "Message",
                            accessorKey: "message",
                          },
                          {
                            header: "Send To",
                            accessorKey: "sendTo",
                          },
                          {
                            header: "Actions",
                            accessorKey: "actions",
                            enableSorting: false,
                            enableColumnFilter: false,
                            cell: ({ row }) => {
                              const notification = row.original;
                              return (
                                // we've added some custom button actions
                                <div className="actions-right">
                                  {/* use this button to add a edit kind of action */}
                                  <Button
                                    variant="warning"
                                    size="sm"
                                    className="text-warning btn-link edit"
                                    onClick={() => handleOpenEdit(notification)}
                                  >
                                    <i className="fa fa-edit" />
                                  </Button>{" "}
                                  <Button
                                    variant="danger"
                                    size="sm"
                                    className="text-danger btn-link edit"
                                    onClick={() =>
                                      handleOpenDelete(notification)
                                    }
                                  >
                                    <i className="fa fa-trash" />
                                  </Button>{" "}
                                </div>
                              );
                            },
                          },
                        ]}
                        /*
                                You can choose between primary-pagination, info-pagination, success-pagination, warning-pagination, danger-pagination or none - which will make the pagination buttons gray
                              */
                        className="-striped -highlight primary-pagination"
                      />
                    </Card.Body>
                  </Card>
                </Col>
                <Col xs="12" style={{ marginTop: 25, textAlign: "right" }}>
                  <Button
                    onClick={() => setModal(!modal)}
                    style={{
                      paddingLeft: 20,
                      paddingRight: 20,
                      paddingTop: 15,
                      paddingBottom: 15,
                      fontSize: 16,
                      fontWeight: "bold",
                    }}
                  >
                    Create Notification
                  </Button>
                </Col>
              </Row>
            </Container>
            <Modal
              className="modal-primary"
              onHide={handleClose}
              show={modal}
              size="md"
            >
              <Modal.Header className="justify-content-center">
                <label>
                  {editNotificationId ? "Edit" : "Add"}/Send Notification
                </label>
              </Modal.Header>
              <Modal.Body>
                <Form action="#" method="#">
                  <Form.Group>
                    <label>Title</label>
                    <Form.Control
                      value={title}
                      placeholder="Title"
                      type="string"
                      onChange={(event) => setTitle(event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <label>Notification Message</label>
                    <Form.Control
                      value={message}
                      placeholder="Message"
                      type="string"
                      onChange={(event) => setMessage(event.target.value)}
                    />
                  </Form.Group>
                  <Form.Group>
                    <label>Notification Target</label>
                    <Select
                      className="react-select info"
                      classNamePrefix="react-select"
                      placeholder="Choose who to send to"
                      name="sendTo"
                      closeMenuOnSelect={true}
                      value={sendTo} // Controlled by the selectedOption state
                      onChange={(value) => setSendTo(value)}
                      options={options}
                      style={{ marginBottom: 10 }}
                    />
                  </Form.Group>
                  {includes(sendTo?.label, "Challenge") && (
                    <Form.Group>
                      <label>Selected Challenge</label>
                      <Select
                        className="react-select info"
                        classNamePrefix="react-select"
                        placeholder="Select a challenge"
                        name="selectedChallenge"
                        closeMenuOnSelect={true}
                        value={selectedChallenge} // Controlled by the selectedOption state
                        onChange={(value) => setSelectedChallenge(value)}
                        options={map(challenges, (challenge) => {
                          return {
                            ...challenge,
                            value: challenge?._id,
                            label: challenge?.title,
                          };
                        })}
                      />
                    </Form.Group>
                  )}

                  <Form.Group controlId="exampleForm.SelectCustom">
                    <Form.Label>Frequency of Notification</Form.Label>
                    <Form.Control
                      as="select"
                      custom
                      value={frequency}
                      onChange={(event) => setFrequency(event.target.value)}
                    >
                      <option value="none">None - Manual sending only</option>
                      <option value="once">Send once at scheduled time</option>
                      <option value="daily">Recurring Daily</option>
                      {/* Add more options as needed */}
                    </Form.Control>
                  </Form.Group>
                  {frequency !== "none" && (
                    <Form.Group controlId="exampleForm.SelectCustom">
                      <Form.Label>Time of Notification</Form.Label>
                      <Form.Control
                        as="select"
                        custom
                        value={time}
                        onChange={(event) =>
                          setTime(Number(event.target.value))
                        }
                      >
                        <option value={0}>Midnight</option>
                        <option value={1}>1 AM</option>
                        <option value={2}>2 AM</option>
                        <option value={3}>3 AM</option>
                        <option value={4}>4 AM</option>
                        <option value={5}>5 AM</option>
                        <option value={6}>6 AM</option>
                        <option value={7}>7 AM</option>
                        <option value={8}>8 AM</option>
                        <option value={9}>9 AM</option>
                        <option value={10}>10 AM</option>
                        <option value={11}>11 AM</option>
                        <option value={12}>Noon</option>
                        <option value={13}>1 PM</option>
                        <option value={14}>2 PM</option>
                        <option value={15}>3 PM</option>
                        <option value={16}>4 PM</option>
                        <option value={17}>5 PM</option>
                        <option value={18}>6 PM</option>
                        <option value={19}>7 PM</option>
                        <option value={20}>8 PM</option>
                        <option value={21}>9 PM</option>
                        <option value={22}>10 PM</option>
                        <option value={23}>11 PM</option>
                        {/* Add more options as needed */}
                      </Form.Control>
                    </Form.Group>
                  )}
                  {frequency === "once" && (
                    <Form.Group>
                      <label>Date to send the notification</label>
                      <DatePicker
                        selected={date}
                        onChange={setDate}
                        placeholderText="Date"
                        customInput={
                          <Form.Control
                            style={{
                              borderColor: "black",
                              color: "black",
                              borderWidth: 1,
                              padding: 10,
                              borderRadius: 50,
                              marginBottom: 10,
                            }}
                          ></Form.Control>
                        }
                      />
                    </Form.Group>
                  )}
                </Form>
              </Modal.Body>
              <div className="modal-footer">
                <Button
                  className="btn-simple"
                  onClick={handleClose}
                  variant="link"
                >
                  Cancel
                </Button>
                <Button
                  className="btn-simple"
                  onClick={editNotificationId ? handleUpdate : handleAdd}
                  variant="outlined"
                  color="primary"
                  disabled={
                    !title ||
                    !message ||
                    !sendTo ||
                    (includes(sendTo?.label, "Challenge") &&
                      !selectedChallenge) ||
                    (frequency === "once" && !date) ||
                    (frequency !== "none" && !time)
                  }
                >
                  Save
                </Button>
                <Button
                  className="btn-simple"
                  onClick={handleSend}
                  variant="outlined"
                  color="primary"
                  disabled={
                    !title ||
                    !message ||
                    !sendTo ||
                    (includes(sendTo?.label, "Challenge") && !selectedChallenge)
                  }
                >
                  Send Now
                </Button>
              </div>
            </Modal>

            <Modal
              className="modal-primary"
              onHide={handleCloseDelete}
              show={Boolean(deleteNotificationId)}
              size="md"
            >
              <Modal.Header className="justify-content-center">
                <label>Delete Notification</label>
              </Modal.Header>
              <Modal.Body>
                Are you sure you want to delete this Notification? This action
                cannot be undone
              </Modal.Body>
              <div className="modal-footer">
                <Button
                  className="btn-simple"
                  onClick={handleCloseDelete}
                  variant="outlined"
                >
                  Cancel
                </Button>
                <Button
                  className="btn-simple"
                  onClick={() => handleDelete(deleteNotificationId)}
                  variant="danger"
                  color="primary"
                >
                  Delete Notification
                </Button>
              </div>
            </Modal>
          </>
        );
      }}
    </Query>
  );
}

export default withSession(NotificationManager);
